import { Signal, TemplateRef } from '@angular/core';
import {
  CellTemplateContext,
  ColumnBuilder,
  createCheckboxConfig,
  DatasetTableConfig,
  deleteAction,
  editAction,
  TableConfigBuilder,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceModels } from '@models/intelligence.model';
import { OrganizationMembersModels } from '@models/organization-members.model';

export type OrganizationMemberCellContext = CellTemplateContext<IntelligenceModels.Organizations.Member>;

export interface CreateOrganizationMembersTableConfigDependencies {
  nameCellTemplate: TemplateRef<OrganizationMemberCellContext>;
  emailCellTemplate: TemplateRef<OrganizationMemberCellContext>;
  permissionsCellTemplate: TemplateRef<OrganizationMemberCellContext>;
  lastLoginCellTemplate: TemplateRef<OrganizationMemberCellContext>;
  selectedMembersCount: Signal<number>;
  currentUserId: Signal<string | null>;
  canManageUserPermissions: Signal<boolean>;
  canRemoveUser: Signal<boolean>;
  canForceLogoutUser: Signal<boolean>;
  canForcePasswordResetUser: Signal<boolean>;
  forceLogoutInProgressByEmail: Signal<Record<string, boolean>>;
  resetPasswordInProgressByEmail: Signal<Record<string, boolean>>;
  isWellsFargoEnv: boolean;
  isDeveloper: Signal<boolean>;
}

/**
 * Mirrors the settings users table, including its RBAC gating. Actions still hide
 * while rows are selected so they don't compete with the selection snackbar.
 */
export function createOrganizationMembersTableConfig(
  deps: CreateOrganizationMembersTableConfigDependencies,
): DatasetTableConfig<IntelligenceModels.Organizations.Member> {
  const builder = new ColumnBuilder<IntelligenceModels.Organizations.Member>();
  const hasAnySelection = (): boolean => deps.selectedMembersCount() > 0;

  return new TableConfigBuilder<IntelligenceModels.Organizations.Member>()
    .columns([
      builder
        .column('lastname')
        .label('USERS.TABLE.NAME')
        .width('minmax(120px, 1fr)')
        .sortable()
        .cellTemplate(deps.nameCellTemplate)
        .build(),

      builder
        .column('email')
        .label('USERS.TABLE.EMAIL')
        .width('minmax(150px, 1.5fr)')
        .sortable()
        .cellTemplate(deps.emailCellTemplate)
        .mobileVisible()
        .cardIcon('ui-icon-mail')
        .build(),

      builder
        .column('groupIds')
        .label('USERS.TABLE.PERMISSIONS')
        .width('minmax(120px, 1.2fr)')
        .cellTemplate(deps.permissionsCellTemplate)
        .build(),

      builder
        .column('lastLogin')
        .label('USERS.TABLE.LAST_ENTRY')
        .width('minmax(120px, 1fr)')
        .sortable()
        .cellTemplate(deps.lastLoginCellTemplate)
        .mobileVisible()
        .cardIcon('ui-icon-calendar')
        .build(),
    ])
    .actions([
      {
        id: OrganizationMembersModels.RowActionId.Permissions,
        icon: 'ui-icon-lock',
        label: 'USERS.TOOLTIPS.PERMISSIONS',
        tooltip: 'USERS.TOOLTIPS.PERMISSIONS',
        tooltipClass: 'sidebar-tooltip',
        visible: () => !hasAnySelection() && deps.canManageUserPermissions(),
        showInline: false,
      },
      {
        id: OrganizationMembersModels.RowActionId.ForceLogout,
        icon: 'ui-icon-logout',
        label: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.TITLE',
        tooltip: 'USERS.CONFIRMATIONS.FORCE_LOGOUT_BULK.TITLE',
        tooltipClass: 'sidebar-tooltip',
        visible: (row) => !hasAnySelection() && !!row.email && deps.canForceLogoutUser(),
        disabled: (row) => !!deps.forceLogoutInProgressByEmail()[row.email],
        showInline: false,
      },
      {
        id: OrganizationMembersModels.RowActionId.ForceResetPassword,
        icon: 'ui-icon-lock',
        label: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_SINGLE.TITLE',
        tooltip: 'USERS.CONFIRMATIONS.FORCE_RESET_PASSWORD_SINGLE.TITLE',
        tooltipClass: 'sidebar-tooltip',
        visible: (row) => !hasAnySelection() && !!row.email && deps.canForcePasswordResetUser(),
        disabled: (row) => !!deps.resetPasswordInProgressByEmail()[row.email],
        showInline: false,
      },
      editAction<IntelligenceModels.Organizations.Member>({
        label: 'USERS.TOOLTIPS.EDIT',
        tooltip: 'USERS.TOOLTIPS.EDIT',
        tooltipClass: 'sidebar-tooltip',
        visible: () =>
          !hasAnySelection() &&
          deps.canManageUserPermissions() &&
          (!deps.isWellsFargoEnv || deps.isDeveloper()),
        showInline: false,
      }),
      deleteAction<IntelligenceModels.Organizations.Member>({
        label: (row) =>
          row.id === deps.currentUserId() ? 'USERS.TOOLTIPS.LEAVE' : 'USERS.TOOLTIPS.REMOVE',
        tooltip: (row) =>
          row.id === deps.currentUserId() ? 'USERS.TOOLTIPS.LEAVE' : 'USERS.TOOLTIPS.REMOVE',
        tooltipClass: 'sidebar-tooltip',
        visible: () =>
          !hasAnySelection() && deps.canRemoveUser() && (!deps.isWellsFargoEnv || deps.isDeveloper()),
        showInline: false,
      }),
    ])
    .checkboxConfig(
      createCheckboxConfig({
        enabled: deps.canForcePasswordResetUser() || deps.canForceLogoutUser(),
        getRowId: (row) => row.email ?? row.id,
        isRowDisabled: (row) =>
          !row.email ||
          !!deps.resetPasswordInProgressByEmail()[row.email] ||
          !!deps.forceLogoutInProgressByEmail()[row.email],
        tooltip: 'Select users',
      }),
    )
    .trackBy('id')
    .rowHeight(56)
    .build();
}
